import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Award, Medal, Target, ChevronRight } from 'lucide-react';
import { teamPerformance } from '../../data/mock';

const RankingsSection = () => {
  return (
    <section data-testid="rankings-section" className="relative py-20 md:py-32 bg-cream-dark overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-pragmatic/20 to-richolive/15 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-dwarfpony text-sm font-semibold tracking-wider uppercase mb-4">
            Our Track Record
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-deepwalnut mb-6">
            <span className="text-dwarfpony">8+ Awards</span> & Counting
          </h2>
          <p className="text-heavyrain text-lg max-w-2xl mx-auto">
            Recognized globally for excellence, innovation, and impact in robotics
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          <div className="bg-gradient-to-br from-pragmatic/20 to-dwarfpony/10 border border-dwarfpony/30 rounded-2xl p-6 text-center">
            <TrendingUp className="w-10 h-10 text-dwarfpony mx-auto mb-3" />
            <p className="text-3xl font-bold text-deepwalnut">Top 20</p>
            <p className="text-heavyrain text-sm mt-1">Best Ranking</p>
          </div>
          <div className="bg-gradient-to-br from-richolive/15 to-richolive/5 border border-richolive/30 rounded-2xl p-6 text-center">
            <Award className="w-10 h-10 text-richolive mx-auto mb-3" />
            <p className="text-3xl font-bold text-deepwalnut">8+</p>
            <p className="text-heavyrain text-sm mt-1">Awards Won</p>
          </div>
          <div className="bg-gradient-to-br from-pragmatic/20 to-dwarfpony/10 border border-dwarfpony/30 rounded-2xl p-6 text-center">
            <Medal className="w-10 h-10 text-dwarfpony mx-auto mb-3" />
            <p className="text-3xl font-bold text-deepwalnut">7</p>
            <p className="text-heavyrain text-sm mt-1">Years Participated</p>
          </div>
          <div className="bg-gradient-to-br from-richolive/15 to-richolive/5 border border-richolive/30 rounded-2xl p-6 text-center">
            <Target className="w-10 h-10 text-richolive mx-auto mb-3" />
            <p className="text-3xl font-bold text-deepwalnut">Top 5</p>
            <p className="text-heavyrain text-sm mt-1">2030 Goal</p>
          </div>
        </div>

        {/* Timeline */}
        <div className="relative">
          <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-dwarfpony via-richolive to-dwarfpony hidden md:block" />
          
          <div className="space-y-8">
            {teamPerformance.slice(0, 4).map((team, index) => (
              <div
                key={team.year}
                className={`flex flex-col md:flex-row items-center gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
              >
                <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                  <Link 
                    to={`/performance/${team.year}`}
                    className={`bg-cream border border-pragmatic/30 rounded-xl p-6 hover:border-dwarfpony hover:shadow-lg transition-all inline-block group ${index % 2 === 0 ? 'md:ml-auto' : 'md:mr-auto'}`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-2xl font-bold text-dwarfpony">{team.year}</span>
                      <span className="text-pragmatic">|</span>
                      <span className="text-heavyrain">{team.location}</span>
                    </div>
                    <p className="text-deepwalnut font-semibold text-lg group-hover:text-dwarfpony transition-colors">{team.team}</p>
                    <p className="text-richolive text-sm">{team.rank}</p>
                    {team.medals.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {team.medals.map((medal, i) => (
                          <span key={i} className="text-xs bg-pragmatic/30 text-dwarfpony-dark px-2 py-1 rounded-full">
                            {medal}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className={`flex items-center gap-1 mt-3 text-richolive text-sm group-hover:text-dwarfpony transition-colors ${index % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                      View Details
                      <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                </div>
                
                {/* Center Dot */}
                <Link 
                  to={`/performance/${team.year}`}
                  className="hidden md:flex w-4 h-4 rounded-full bg-gradient-to-br from-dwarfpony to-richolive border-4 border-cream shadow-lg z-10 hover:scale-125 transition-transform"
                />
                
                <div className="flex-1" />
              </div>
            ))}
          </div>
        </div>

        {/* View All Link */}
        <div className="text-center mt-12">
          <Link
            to="/performance"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-dwarfpony to-dwarfpony-dark text-white font-semibold px-6 py-3 rounded-full hover:from-dwarfpony-dark hover:to-dwarfpony transition-all shadow-lg shadow-dwarfpony/25"
          >
            View All Years
            <ChevronRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default RankingsSection;
